import { useEffect } from "react";

/**
 * Per-route head manager for social/share metadata.
 * Mirrors the JsonLd head-injection pattern (no extra dependency) so each
 * route can expose its own title, og:*, twitter:* and self-referencing url.
 * On unmount the per-route tags are removed so the static index.html
 * fallback applies again.
 */
interface SeoProps {
  title?: string;
  description?: string;
  /** Path or absolute url; relative paths are resolved against podgo.io. */
  url?: string;
  image?: string;
  type?: "website" | "article";
  /** When true, injects `<meta name="robots" content="noindex, nofollow">`. */
  noindex?: boolean;
}

const SITE_ORIGIN = "https://podgo.io";
const PRODUCTION_HOSTS = ["podgo.io", "www.podgo.io"];

/** Staging/preview hosts must never be indexed, whatever the route asks for. */
function isProductionHost(): boolean {
  if (typeof window === "undefined") return true;
  return PRODUCTION_HOSTS.includes(window.location.hostname);
}

/** Search engines truncate past ~160 chars; cut at a word boundary instead. */
const MAX_DESCRIPTION = 160;

const NAMED_ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
};

/**
 * RSS-sourced copy ships markup and escaped markup. Entities are decoded first
 * so `&lt;br /&gt;` becomes a real tag the strip step can remove.
 */
export function toMetaDescription(value?: string | null, max = MAX_DESCRIPTION): string | undefined {
  if (!value) return undefined;
  let text = String(value)
    .replace(/&(nbsp|amp|lt|gt|quot|#39|apos);/g, (m) => NAMED_ENTITIES[m] ?? " ")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
  text = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (!text) return undefined;
  if (text.length <= max) return text;
  const slice = text.slice(0, max - 1);
  const cut = slice.lastIndexOf(" ");
  return `${(cut > 80 ? slice.slice(0, cut) : slice).replace(/[.,;:!?–—-]+$/, "")}…`;
}

function toAbsolute(value?: string): string | undefined {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_ORIGIN}${value.startsWith("/") ? "" : "/"}${value}`;
}

function setMeta(attr: "name" | "property", key: string, content?: string) {
  if (!content) return () => {};
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  const created = !el;
  const previous = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
    } else if (previous !== null) {
      el.setAttribute("content", previous);
    }
  };
}

/**
 * Pages without their own image must not inherit the previous route's cover art,
 * so the tag is emptied while mounted and restored (index.html default) on exit.
 */
function clearMeta(attr: "name" | "property", key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) return () => {};
  const previous = el.getAttribute("content");
  el.setAttribute("content", "");
  return () => {
    if (previous !== null) el.setAttribute("content", previous);
  };
}

function setCanonical(href?: string) {
  if (!href) return () => {};
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  const previous = el?.getAttribute("href") ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (!el) return;
    if (created) {
      el.remove();
    } else if (previous !== null) {
      el.setAttribute("href", previous);
    }
  };
}

export function Seo({ title, description, url, image, type = "website", noindex = false }: SeoProps) {
  useEffect(() => {
    const absUrl = toAbsolute(url);
    const absImage = toAbsolute(image);
    const metaDescription = toMetaDescription(description);

    const previousTitle = document.title;
    if (title) document.title = title;

    const cleanups = [
      setMeta("name", "description", metaDescription),
      setMeta("property", "og:type", type),
      setMeta("property", "og:title", title),
      setMeta("name", "twitter:title", title),
      setMeta("property", "og:description", metaDescription),
      setMeta("name", "twitter:description", metaDescription),
      setMeta("property", "og:url", absUrl),
      absImage
        ? setMeta("property", "og:image", absImage)
        : clearMeta("property", "og:image"),
      absImage
        ? setMeta("name", "twitter:image", absImage)
        : clearMeta("name", "twitter:image"),
      setMeta(
        "name",
        "robots",
        noindex || !isProductionHost() ? "noindex, nofollow" : "index, follow"
      ),
      setCanonical(absUrl),
    ];

    return () => {
      document.title = previousTitle;
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, url, image, type, noindex]);

  return null;
}
